import {
  connect,
  ConnectorNotFoundError,
  createConfig,
  disconnect,
  getConnections,
  getConnectors,
  InjectedParameters,
  reconnect,
  switchChain,
  watchAccount,
  watchChainId,
} from '@wagmi/core';
import { isWithCode } from '@orionprotocol/utils';
import { JsonRpcSigner } from 'ethers';
import { injected } from '@wagmi/connectors';

import { getEthersSigner } from '../ethersAdapters';
import { ConnectSuccessData, GetWalletAPI } from '../WalletAPI';
import {
  WalletAPIError,
  WalletChangeNetworkFailed,
  WalletConnectCancelled,
  WalletDisconnectFailed,
} from '../walletAPIErrors';
import { Logger } from '../Logger';
import { availableChains, transports } from '../availableChains';
import { WalletsNames } from '../wallets';

export enum InjectedWalletsNames {
  Metamask = 'MetaMask', // all chains
  OKX = 'OKX Wallet', // all chains
  SafePal = 'SafePal', // all chains
  TokenPocket = 'TokenPocket',
  Rabby = 'Rabby Wallet',
  TrustWallet = 'Trust Wallet', // NOTE: please check if it works properly every time you see that
  BitgetWallet = 'Bitget Wallet',
}

// Chrome extensions for wallets to install
const chromeExtensionsMap: Partial<Record<WalletsNames, string>> = {
  [WalletsNames.Bitget]:
    'https://chromewebstore.google.com/detail/bitget-wallet-formerly-bi/jiidiaalihmmhddjgbnbgdfflelocpak',
  [WalletsNames.TrustWallet]:
    'https://chromewebstore.google.com/detail/trust-wallet/egjidjbpglichdcondbcbdnbeeppgdph',
  [WalletsNames.Coinbase]:
    'https://chromewebstore.google.com/detail/coinbase-wallet-extension/hnfanknocfeofbddgcijnmhnfnkdnaad',
  [WalletsNames.TokenPocket]:
    'https://chromewebstore.google.com/detail/tokenpocket-web3-nostr-wa/mfgccjchihfkkindfppnaooecgfneiii',
  [WalletsNames.Rabby]:
    'https://chromewebstore.google.com/detail/rabby-wallet/acmacodkjbdgmoleebolmdjonilkdbch',
  [WalletsNames.SafePal]:
    'https://chromewebstore.google.com/detail/safepal-extension-wallet/lgmpcpglpngdoalbgeoldeajfclnhafa',
  [WalletsNames.OKX]:
    'https://chromewebstore.google.com/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge',
};

export function createInjectedAPI({
  logger = console,
  target,
  name,
}: {
  logger?: Logger;
  target?: InjectedParameters['target']; // for EIP-1193 Ethereum Provider to target.
  name?: InjectedWalletsNames;
} = {}): GetWalletAPI {
  const config = createConfig({
    chains: availableChains,
    connectors: [injected({ target })],
    transports,
    multiInjectedProviderDiscovery: true,
  });

  return ({ type, onAccountChanged, onChainChanged, onDisconnected }) => {
    const unwatchAccounts = watchAccount(config, {
      onChange(data) {
        onAccountChanged?.({
          address: data.address,
          chainId: data.chainId,
        });
      },
    });

    const unwatchChainId = watchChainId(config, {
      onChange(chainId) {
        onChainChanged?.({
          chainId,
        });
      },
    });

    const connections = getConnections(config);
    const connectors = getConnectors(config);
    const currentConnector = connectors.find(
      (connector) => connector.name === name,
    );

    return {
      type,
      getSigner: (): Promise<JsonRpcSigner | undefined> =>
        getEthersSigner(config, {
          connector: currentConnector,
        }),
      connect: async (): Promise<ConnectSuccessData> => {
        const isExtInstalled = connectors.find((c) => {
          return name && c.name.toLowerCase().includes(name.toLowerCase());
        });
        if (!isExtInstalled && type in chromeExtensionsMap) {
          const chromeExtUrl = chromeExtensionsMap[type as WalletsNames];
          window.open(chromeExtUrl, '_blank');
          throw new Error(`Please install ${type} extension to use it.`);
        }

        try {
          if (currentConnector !== undefined) {
            if (connections.length !== 0) {
              const reconnectData = await reconnect(config, {
                connectors: [currentConnector],
              });
              return {
                address: reconnectData?.[0]?.accounts[0].toLowerCase(),
                chainId: reconnectData?.[0]?.chainId,
              };
            }

            const { accounts, chainId } = await connect(config, {
              connector: currentConnector,
            });
            return {
              address: accounts[0].toLowerCase(),
              chainId,
            };
          }

          const { accounts, chainId } = await connect(config, {
            connector: injected({
              target,
            }),
          });
          return {
            address: accounts[0].toLowerCase(),
            chainId,
          };
        } catch (e) {
          if (e instanceof ConnectorNotFoundError) {
            throw new Error(`Please install ${type} extension to use it.`);
          }

          const code = isWithCode(e) ? e.code : undefined;

          if (code === 4001) {
            throw new WalletConnectCancelled(e, logger);
          }

          if (code === -32002) {
            throw new Error(
              'The wallet is already being processed. Please check your wallet.',
            );
          }

          throw new WalletAPIError(e, logger);
        }
      },
      disconnect: async (): Promise<void> => {
        try {
          await disconnect(config);
          unwatchAccounts();
          unwatchChainId();
          onDisconnected?.();
        } catch (e) {
          throw new WalletDisconnectFailed(e, logger);
        }
      },
      changeNetwork: async ({
        chainId,
      }: {
        chainId: string;
      }): Promise<void> => {
        try {
          await switchChain(config, { chainId: Number(chainId) });
        } catch (e) {
          throw new WalletChangeNetworkFailed(e, logger);
        }
      },
    };
  };
}
